export type HopDestinationType = "CUSTOMER" | "SUPPLIER";

export type HopDestinationPayload = {
  type: HopDestinationType;
  customerId?: string;
  supplierId?: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude?: number;
      longitude?: number;
    };
    contact?: {
      name?: string;
      phoneNumber?: string;
    };
  };
};

/** Group key so orders with the same destination share one shipment. */
export function destinationGroupKey(destination: HopDestinationPayload): string {
  const id =
    destination.type === "CUSTOMER"
      ? destination.customerId || ""
      : destination.supplierId || "";
  const line = destination.address?.addressLine1 || "";
  return `${destination.type}:${id}:${line}`;
}

/**
 * True when every order has a destination payload (destinations may differ).
 */
export function everyOrderHasDestination(
  orderNos: string[],
  destinationsByOrder?: Record<string, HopDestinationPayload>,
): boolean {
  if (!orderNos.length || !destinationsByOrder) return false;
  return orderNos.every((orderNo) => !!destinationsByOrder[orderNo]);
}

/**
 * Returns shared destinationGroupKey when every order has a destination and
 * they all match. Otherwise null (incomplete or mixed).
 */
export function sharedDestinationHash(
  orderNos: string[],
  destinationsByOrder?: Record<string, HopDestinationPayload>,
): string | null {
  if (!orderNos.length || !destinationsByOrder) return null;
  let shared: string | null = null;
  for (const orderNo of orderNos) {
    const dest = destinationsByOrder[orderNo];
    if (!dest) return null;
    const key = destinationGroupKey(dest);
    if (!shared) shared = key;
    else if (key !== shared) return null;
  }
  return shared;
}

/** Porter drop override from a hop destination payload. */
export function hopDestinationToPorterDrop(destination: HopDestinationPayload): {
  lat: number;
  lng: number;
  name: string;
  mobileNumber: string;
} | null {
  const lat = destination.address?.location?.latitude;
  const lng = destination.address?.location?.longitude;
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
    return null;
  }
  const name =
    destination.address?.contact?.name?.trim() ||
    (destination.type === "SUPPLIER" ? "Supplier" : "Customer");
  const mobileNumber =
    destination.address?.contact?.phoneNumber?.trim() || "";
  if (!mobileNumber) return null;
  return {
    lat: Number(lat),
    lng: Number(lng),
    name,
    mobileNumber,
  };
}

/**
 * Build hop destination payload from order.logisticsPath isDestination entry
 * (set at R2D Self Dispatch). Used to batch Create Shipment by path dest.
 */
export function hopDestinationFromLogisticsPath(
  logisticsPath?: Array<{
    type?: string;
    customerId?: string | { toString(): string };
    supplierId?: string | { toString(): string };
    isDestination?: boolean;
    address?: HopDestinationPayload["address"];
  }> | null,
): HopDestinationPayload | null {
  if (!Array.isArray(logisticsPath)) return null;
  const dest = logisticsPath.find((e) => e?.isDestination === true);
  if (!dest?.type || !dest.address?.addressLine1) return null;

  const type = dest.type === "SUPPLIER" ? "SUPPLIER" : "CUSTOMER";
  const customerId =
    dest.customerId != null ? String(dest.customerId) : undefined;
  const supplierId =
    dest.supplierId != null ? String(dest.supplierId) : undefined;

  if (type === "CUSTOMER" && !customerId) return null;
  if (type === "SUPPLIER" && !supplierId) return null;

  return {
    type,
    ...(customerId ? { customerId } : {}),
    ...(supplierId ? { supplierId } : {}),
    address: { ...dest.address },
  };
}

/** Current SUPPLIER stop on logisticsPath (where the order sits now). */
export function getCurrentLocationSupplierId(
  logisticsPath?: Array<{
    type?: string;
    supplierId?: string | { toString(): string };
    isCurrentLocation?: boolean;
  }> | null,
): string | null {
  if (!Array.isArray(logisticsPath)) return null;
  const current = logisticsPath.find((e) => e?.isCurrentLocation === true);
  if (!current || current.type !== "SUPPLIER") return null;
  return current.supplierId != null ? String(current.supplierId) : null;
}

/** True after at least one intermediate hop shipment completed. */
export function hasCompletedPriorHop(
  logisticsPath?: Array<{ completedShipmentId?: string }> | null,
): boolean {
  if (!Array.isArray(logisticsPath)) return false;
  return logisticsPath.some((e) => Boolean(e?.completedShipmentId));
}

export function getCustomerPickupPointIds(customer?: {
  pickupPoints?: Array<{
    pickupPointId?: string | { toString(): string };
    isDefault?: boolean;
  }>;
}): string[] {
  if (!Array.isArray(customer?.pickupPoints)) return [];
  return customer.pickupPoints
    .map((p) =>
      p?.pickupPointId != null ? String(p.pickupPointId) : "",
    )
    .filter(Boolean);
}

/**
 * Later hop only: if current supplier matches a customer pickup point,
 * destination is locked to the order customer (cannot change).
 *
 * When pickupPoints are missing on stale orderData, falls back to locking
 * only if activeSupplierId (FROM / grouping supplier) equals the current
 * location supplier — i.e. the order is being dispatched from that stop.
 */
export function isHopDestinationLockedToCustomer(
  orderData: {
    logisticsPath?: Array<{
      type?: string;
      supplierId?: string | { toString(): string };
      isCurrentLocation?: boolean;
      completedShipmentId?: string;
    }> | null;
    customer?: {
      pickupPoints?: Array<{
        pickupPointId?: string | { toString(): string };
        isDefault?: boolean;
      }>;
    };
  },
  opts?: { activeSupplierId?: string },
): boolean {
  if (!hasCompletedPriorHop(orderData.logisticsPath)) return false;
  const currentSupplierId = getCurrentLocationSupplierId(
    orderData.logisticsPath,
  );
  if (!currentSupplierId) return false;

  const pickupIds = getCustomerPickupPointIds(orderData.customer);
  if (pickupIds.length > 0) {
    return pickupIds.includes(currentSupplierId);
  }

  const activeSupplierId = opts?.activeSupplierId
    ? String(opts.activeSupplierId)
    : "";
  return Boolean(activeSupplierId && activeSupplierId === currentSupplierId);
}

/** True when every selected order is locked to the customer destination. */
export function areAllOrdersLockedToCustomerDestination(
  orderNos: string[],
  orderDataMap?: Record<string, any>,
  opts?: { activeSupplierId?: string },
): boolean {
  if (!orderNos.length) return false;
  return orderNos.every((orderNo) =>
    isHopDestinationLockedToCustomer(orderDataMap?.[orderNo] || {}, opts),
  );
}

/**
 * Build customer destination payloads/keys for locked orders.
 * Returns null if any order cannot build a customer destination.
 */
export function buildCustomerDestinationsForOrders(
  orderNos: string[],
  orderDataMap?: Record<string, any>,
  opts?: { customerNameFallback?: string },
): {
  destinationsByOrder: Record<string, HopDestinationPayload>;
  destinationKeysByOrder: Record<string, string>;
} | null {
  const destinationsByOrder: Record<string, HopDestinationPayload> = {};
  const destinationKeysByOrder: Record<string, string> = {};

  for (const orderNo of orderNos) {
    const raw = orderDataMap?.[orderNo] || {};
    const orderData = {
      ...raw,
      customer: {
        ...(raw.customer || {}),
        name:
          raw.customer?.name?.trim() ||
          opts?.customerNameFallback ||
          "Customer",
      },
    };
    const payload = buildCustomerHopDestination(orderData);
    if (!payload?.customerId) return null;
    destinationsByOrder[orderNo] = payload;
    destinationKeysByOrder[orderNo] = `CUSTOMER:${payload.customerId}`;
  }

  return { destinationsByOrder, destinationKeysByOrder };
}

export function buildCustomerHopDestination(orderData: {
  customerId?: string;
  customer?: { _id?: string; name?: string; mobileNumber?: string };
  shipmentAddress?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    location?: { latitude?: number; longitude?: number };
  };
}): HopDestinationPayload | null {
  const customerId =
    orderData.customerId?.toString?.() ||
    orderData.customer?._id?.toString?.() ||
    "";
  const addr = orderData.shipmentAddress;
  // Require id + at least a street line; city/state may be empty on some orders
  if (!customerId || !addr?.addressLine1?.trim()) {
    return null;
  }
  return {
    type: "CUSTOMER",
    customerId,
    address: {
      addressLine1: addr.addressLine1.trim(),
      ...(addr.addressLine2 ? { addressLine2: addr.addressLine2 } : {}),
      city: addr.city?.trim() || "",
      state: addr.state?.trim() || "",
      ...(addr.pinCode ? { pinCode: addr.pinCode } : {}),
      ...(addr.country ? { country: addr.country } : {}),
      ...(addr.location ? { location: { ...addr.location } } : {}),
      contact: {
        name: orderData.customer?.name || "Customer",
        phoneNumber: orderData.customer?.mobileNumber || "",
      },
    },
  };
}

export function buildSupplierHopDestination(supplier: {
  _id?: string;
  businessName?: string;
  name?: string;
  mobileNumber?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    location?: { latitude?: number; longitude?: number };
  };
  branches?: Array<{
    _id?: string;
    branchName?: string;
    branchPrimaryMobileNumber?: string;
    branchAddress?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      pinCode?: string;
      country?: string;
      location?: { latitude?: number; longitude?: number };
    };
  }>;
  selectedBranchId?: string;
}): HopDestinationPayload | null {
  const supplierId = supplier._id?.toString?.() || "";
  if (!supplierId) return null;

  let addr = supplier.address;
  let contactName = supplier.businessName || supplier.name || "Supplier";
  let phone = supplier.mobileNumber || "";

  if (supplier.selectedBranchId && Array.isArray(supplier.branches)) {
    const branch = supplier.branches.find(
      (b) => b._id?.toString?.() === supplier.selectedBranchId,
    );
    if (branch?.branchAddress) {
      addr = branch.branchAddress;
      contactName = branch.branchName || contactName;
      phone = branch.branchPrimaryMobileNumber || phone;
    }
  }

  if (!addr?.addressLine1 || !addr?.city || !addr?.state) {
    return null;
  }

  return {
    type: "SUPPLIER",
    supplierId,
    address: {
      addressLine1: addr.addressLine1,
      ...(addr.addressLine2 ? { addressLine2: addr.addressLine2 } : {}),
      city: addr.city,
      state: addr.state,
      ...(addr.pinCode ? { pinCode: addr.pinCode } : {}),
      ...(addr.country ? { country: addr.country } : {}),
      ...(addr.location ? { location: { ...addr.location } } : {}),
      contact: {
        name: contactName,
        phoneNumber: phone,
      },
    },
  };
}
